import type { Moneda } from "@tora/shared";
import {
  forwardRef,
  type HTMLAttributes,
  type KeyboardEvent,
  type ReactNode,
  type TdHTMLAttributes,
  type ThHTMLAttributes,
} from "react";
import { Importe, type TamanoImporte } from "@/components/money/importe";
import { EstadoVacio } from "@/components/ui/estado-vacio";
import { cn } from "@/lib/utils";

/**
 * Ver MASTER.md seccion 6: densidad de tablas de ERP.
 * `auto` es comoda debajo de md y normal desde md (CSS en globals.css);
 * `compacta` se elige por tabla (stock, listas, mayor), nunca por default.
 */
export type Densidad = "auto" | "comoda" | "normal" | "compacta";

export interface TableProps extends HTMLAttributes<HTMLTableElement> {
  densidad?: Densidad;
  /**
   * `acotado` limita el alto del contenedor y scrollea adentro (reemplaza los
   * max-h a mano). `lleno` no pone techo: se lleva el alto que le deja el
   * padre, que es lo que hace `<Pagina alto="fijo">`.
   */
  alto?: "libre" | "acotado" | "lleno";
  /** Clases del contenedor con scroll. */
  contenedorClassName?: string;
}

export const Table = forwardRef<HTMLTableElement, TableProps>(
  ({ className, densidad = "auto", alto = "libre", contenedorClassName, ...props }, ref) => (
    <div
      data-densidad={densidad}
      className={cn(
        "relative w-full overflow-x-auto",
        alto === "acotado" && "max-h-[min(36rem,70dvh)] overflow-y-auto",
        /*
          `lleno` no pone techo propio —el techo es el padre— asi que la tabla
          nunca pide mas alto del que hay y el tfoot sticky queda a la vista
          siempre, que es todo el punto: un sticky debajo del pliegue no sirve.

          Van las tres clases porque el padre puede ser cualquiera de las dos
          cosas: una caja comun con el alto ya resuelto (el ultimo hijo de
          `<Pagina alto="fijo">`) manda `h-full`, y una columna flex —una Card
          con cabecera propia arriba de la tabla— manda `flex-1`. `min-h-0` es
          el de siempre: sin el, un hijo flex no baja de su alto de contenido y
          el `overflow-y-auto` no se activa nunca (`__root.tsx:110-111`).
        */
        alto === "lleno" && "h-full min-h-0 flex-1 overflow-y-auto",
        contenedorClassName,
      )}
    >
      <table ref={ref} className={cn("w-full caption-bottom text-sm", className)} {...props} />
    </div>
  ),
);
Table.displayName = "Table";

export const TableHeader = forwardRef<
  HTMLTableSectionElement,
  HTMLAttributes<HTMLTableSectionElement>
>(({ className, ...props }, ref) => (
  <thead
    ref={ref}
    className={cn("sticky top-0 z-10 bg-muted [&_tr]:border-b", className)}
    {...props}
  />
));
TableHeader.displayName = "TableHeader";

export const TableBody = forwardRef<
  HTMLTableSectionElement,
  HTMLAttributes<HTMLTableSectionElement>
>(({ className, ...props }, ref) => (
  <tbody ref={ref} className={cn("[&_tr:last-child]:border-0", className)} {...props} />
));
TableBody.displayName = "TableBody";

export const TableFooter = forwardRef<
  HTMLTableSectionElement,
  HTMLAttributes<HTMLTableSectionElement>
>(({ className, ...props }, ref) => (
  <tfoot
    ref={ref}
    className={cn(
      "sticky bottom-0 z-10 border-t bg-muted font-medium [&>tr]:last:border-b-0",
      className,
    )}
    {...props}
  />
));
TableFooter.displayName = "TableFooter";

export interface TableRowProps extends HTMLAttributes<HTMLTableRowElement> {
  /**
   * La fila abre y cierra un detalle propio (`TableFilaDetalle`) abajo.
   *
   * Es apretar la fila entera y no un botoncito aparte: en una tabla compacta
   * una flecha de 32 px por fila es una columna de ruido, y el detalle no es
   * una accion —no cambia nada— sino mirar mas de cerca. La fila queda
   * enfocable y contesta a Enter y a Espacio, que es lo que la vuelve usable
   * sin mouse; el chevron que se dibuja al lado del nombre es la señal de que
   * hay algo abajo, no un control aparte.
   *
   * Los botones que viven adentro de la fila tienen que frenar la propagacion:
   * editar no es desplegar.
   */
  desplegable?: boolean;
  abierta?: boolean;
  onDesplegar?: () => void;
}

export const TableRow = forwardRef<HTMLTableRowElement, TableRowProps>(
  ({ className, desplegable = false, abierta, onDesplegar, ...props }, ref) => (
    <tr
      ref={ref}
      className={cn(
        "border-b border-border transition-colors duration-150 hover:bg-accent/50 data-[state=selected]:bg-accent",
        desplegable &&
          "cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring",
        abierta && "bg-accent/40",
        className,
      )}
      {...(desplegable
        ? {
            tabIndex: 0,
            "aria-expanded": abierta ?? false,
            onClick: onDesplegar,
            onKeyDown: (e: KeyboardEvent<HTMLTableRowElement>) => {
              if (e.key !== "Enter" && e.key !== " ") return;
              e.preventDefault();
              onDesplegar?.();
            },
          }
        : {})}
      {...props}
    />
  ),
);
TableRow.displayName = "TableRow";

/**
 * El detalle que abre una fila desplegable: una fila mas, del ancho de la
 * tabla, con el fondo apagado para que se lea como parte de la de arriba y no
 * como un registro nuevo.
 */
export function TableFilaDetalle({
  colSpan,
  children,
}: {
  colSpan: number;
  children: ReactNode;
}): JSX.Element {
  return (
    <tr className="border-b border-border bg-muted/40">
      <td colSpan={colSpan} className="px-3 py-2">
        {children}
      </td>
    </tr>
  );
}

export const TableHead = forwardRef<HTMLTableCellElement, ThHTMLAttributes<HTMLTableCellElement>>(
  ({ className, ...props }, ref) => (
    <th
      ref={ref}
      className={cn(
        "h-10 whitespace-nowrap px-3 text-left align-middle text-xs font-medium uppercase tracking-wide text-muted-foreground",
        className,
      )}
      {...props}
    />
  ),
);
TableHead.displayName = "TableHead";

export const TableCell = forwardRef<HTMLTableCellElement, TdHTMLAttributes<HTMLTableCellElement>>(
  ({ className, ...props }, ref) => (
    <td ref={ref} className={cn("px-3 align-middle", className)} {...props} />
  ),
);
TableCell.displayName = "TableCell";

export interface TableCellImporteProps extends TdHTMLAttributes<HTMLTableCellElement> {
  /** Con `monto` y `moneda` la celda pinta el <Importe> sola; sin ellos, solo alinea. */
  monto?: number;
  moneda?: Moneda;
  /** Apagar SOLO si el encabezado de la columna ya declara la moneda. */
  codigo?: boolean;
  tamano?: TamanoImporte;
  signoExplicito?: boolean;
}

/** Celda de plata: siempre a la derecha y con cifras tabulares. */
export const TableCellImporte = forwardRef<HTMLTableCellElement, TableCellImporteProps>(
  ({ className, monto, moneda, codigo, tamano, signoExplicito, children, ...props }, ref) => (
    <td ref={ref} className={cn("celda-importe px-3 align-middle", className)} {...props}>
      {monto !== undefined && moneda !== undefined ? (
        <Importe
          monto={monto}
          moneda={moneda}
          codigo={codigo}
          tamano={tamano}
          signoExplicito={signoExplicito}
        />
      ) : (
        children
      )}
    </td>
  ),
);
TableCellImporte.displayName = "TableCellImporte";

export function TableVacia({
  colSpan,
  mensaje,
  accion,
}: {
  colSpan: number;
  mensaje: string;
  /** "Limpiar filtros", "Cargar el primero". */
  accion?: ReactNode;
}): JSX.Element {
  return (
    <tr>
      <td colSpan={colSpan} className="px-3">
        <EstadoVacio
          tamano="sm"
          titulo={mensaje}
          accion={accion}
          className="items-center py-12 text-center"
        />
      </td>
    </tr>
  );
}
